/**
 * A store-only .zip writer: every file is kept byte for byte (method 0, no
 * compression), with its CRC-32. Enough for the CNC batch download — a few
 * dozen text files and .3dm archives — without a dependency.
 *
 * Store-only on purpose: a program is saved exactly as the viewer wrote it
 * (CRLF and all), and an archive that never transforms its contents cannot
 * get them wrong. No ZIP64, so the whole archive stays under 4 GiB and 65,535
 * entries; `zipStore` refuses anything bigger rather than writing a file
 * every unzip would reject.
 *
 * Pure: no DOM. `scripts/check-cnc-ui.ts` writes an archive and has the
 * system `unzip -t` test it.
 */

export interface ZipEntry {
  /** Path inside the archive: forward slashes, no leading "/", no "..". */
  name: string;
  /** A string is written as UTF-8. */
  data: Uint8Array | string;
}

const CRC_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** CRC-32 (IEEE 802.3, the zip one) of `data`. */
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS date and time words, local time, 2-second resolution (the format's own). */
function dosDateTime(d: Date): { date: number; time: number } {
  const year = Math.min(2107, Math.max(1980, d.getFullYear()));
  return {
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
  };
}

/** Why `name` is not a safe archive path, or null. */
function badName(name: string): string | null {
  if (!name) return "an empty name";
  if (name.startsWith("/") || /^[A-Za-z]:/.test(name)) return "an absolute path";
  if (name.includes("\\")) return "a backslash";
  if (name.split("/").some((part) => part === ".." || part === "")) return "an empty or '..' path part";
  return null;
}

const MAX_U32 = 0xffffffff;

/**
 * The archive, as bytes. Entries are written in the order given; `date`
 * stamps every one of them (pass a fixed date for a reproducible archive).
 * Throws on a duplicate or unsafe name, and on anything ZIP64 would need.
 */
export function zipStore(entries: readonly ZipEntry[], date: Date = new Date()): Uint8Array<ArrayBuffer> {
  if (entries.length > 0xffff) throw new Error(`zip: ${entries.length} entries — at most 65535 without ZIP64`);
  const enc = new TextEncoder();
  const { date: dosDate, time: dosTime } = dosDateTime(date);

  const seen = new Set<string>();
  const files = entries.map((e) => {
    const why = badName(e.name);
    if (why) throw new Error(`zip: "${e.name}" is ${why}`);
    if (seen.has(e.name)) throw new Error(`zip: "${e.name}" is in the archive twice`);
    seen.add(e.name);
    const name = enc.encode(e.name);
    const data = typeof e.data === "string" ? enc.encode(e.data) : e.data;
    // Bit 11: the name is UTF-8. Only set when it needs to be, so an
    // all-ASCII archive is byte-identical to what any old tool writes.
    const flags = /^[\x20-\x7e]*$/.test(e.name) ? 0 : 0x0800;
    return { name, data, crc: crc32(data), flags };
  });

  const localSize = files.reduce((n, f) => n + 30 + f.name.length + f.data.length, 0);
  const centralSize = files.reduce((n, f) => n + 46 + f.name.length, 0);
  const total = localSize + centralSize + 22;
  if (localSize > MAX_U32 || total > MAX_U32) throw new Error("zip: the archive would pass 4 GiB — ZIP64 is not supported");

  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let at = 0;
  const u16 = (v: number) => { view.setUint16(at, v, true); at += 2; };
  const u32 = (v: number) => { view.setUint32(at, v >>> 0, true); at += 4; };
  const bytes = (b: Uint8Array) => { out.set(b, at); at += b.length; };

  const offsets: number[] = [];
  for (const f of files) {
    offsets.push(at);
    u32(0x04034b50); // local file header
    u16(10); // version needed: 1.0, stored
    u16(f.flags);
    u16(0); // method: stored
    u16(dosTime);
    u16(dosDate);
    u32(f.crc);
    u32(f.data.length); // compressed size = size
    u32(f.data.length);
    u16(f.name.length);
    u16(0); // extra field length
    bytes(f.name);
    bytes(f.data);
  }

  const centralStart = at;
  files.forEach((f, i) => {
    u32(0x02014b50); // central directory header
    u16(20); // version made by: MS-DOS, 2.0
    u16(10);
    u16(f.flags);
    u16(0);
    u16(dosTime);
    u16(dosDate);
    u32(f.crc);
    u32(f.data.length);
    u32(f.data.length);
    u16(f.name.length);
    u16(0); // extra
    u16(0); // comment
    u16(0); // disk number start
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(offsets[i]);
    bytes(f.name);
  });

  const centralLength = at - centralStart;
  u32(0x06054b50); // end of central directory
  u16(0); // this disk
  u16(0); // the disk the central directory starts on
  u16(files.length);
  u16(files.length);
  u32(centralLength);
  u32(centralStart);
  u16(0); // comment length
  return out;
}
